// « Essaie, pour voir » — la démo animée du blocage (9 oct. 2026).
//
// Un téléphone qui joue la scène en boucle, en trois temps :
//   1. il tape l'adresse dans la barre de recherche (lettre par lettre) ;
//   2. la page de blocage SELFCNTRL s'affiche ;
//   3. il essaie de supprimer le profil dans Réglages — bouton grisé, la
//      suppression passe par une demande depuis le compte, avec le délai.
// Le délai affiché vient de t.tryIt (72 h = REMOVAL_DELAY_HOURS côté back,
// middleware/removal_helpers.php — les deux doivent rester alignés).
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '../../lib/i18n'

const PUB = process.env.PUBLIC_URL || ''
const SCENES = ['type', 'block', 'remove']
const DUR = { type: 3400, block: 3000, remove: 4200 }

function useInView (ref) {
  const [on, setOn] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || !('IntersectionObserver' in window)) { setOn(true); return }
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold: 0.3 })
    io.observe(el)
    return () => io.disconnect()
  }, [ref])
  return on
}

export default function TryIt () {
  const t = useT().tryIt
  const box = useRef(null)
  const visible = useInView(box)
  const [scene, setScene] = useState(0)
  const [typed, setTyped] = useState(0)
  const cur = SCENES[scene]
  const touch = useRef(null)
  function go (i) { setScene((i + SCENES.length) % SCENES.length); setTyped(0) }
  const swipe = {
    onTouchStart: e => { touch.current = e.touches[0].clientX },
    onTouchEnd: e => {
      if (touch.current == null) return
      const dx = e.changedTouches[0].clientX - touch.current
      touch.current = null
      if (Math.abs(dx) > 40) go(scene + (dx < 0 ? 1 : -1))
    }
  }

  // Enchaînement des scènes (seulement quand la section est à l'écran).
  useEffect(() => {
    if (!visible) return
    const id = setTimeout(() => { setScene(s => (s + 1) % SCENES.length); setTyped(0) }, DUR[cur])
    return () => clearTimeout(id)
  }, [visible, cur, scene])

  // La frappe, lettre par lettre.
  useEffect(() => {
    if (!visible || cur !== 'type' || typed >= t.query.length) return
    const id = setTimeout(() => setTyped(n => n + 1), 95)
    return () => clearTimeout(id)
  }, [visible, cur, typed, t.query.length])

  return (
    <section className="lp-sec lp-try" id="demo">
      <div className="lp-wrap lp-try__in">
        <div className="lp-try__txt lp-rv">
          <span className="lp-kicker">{t.kicker}</span>
          <h2 className="lp-h2">{t.title}</h2>
          <p className="lp-lead">{t.lead}</p>
          <ol className="lp-try__steps">
            {t.steps.map((s, i) => (
              <li key={s} className={i === scene ? 'is-on' : ''}><i>{i + 1}</i>{s}</li>
            ))}
          </ol>
        </div>

        <div className="lp-try__stage" ref={box} {...swipe}>
          {/* Téléphone : UNE étape à la fois, qu'on fait glisser (ou qui
              avance seule avec l'animation). */}
          <div className="lp-try__now">
            <button type="button" onClick={() => go(scene - 1)} aria-label="Previous">‹</button>
            <div className="lp-try__nowtx" key={scene}>
              <i>{scene + 1}/{SCENES.length}</i><b>{t.steps[scene]}</b>
            </div>
            <button type="button" onClick={() => go(scene + 1)} aria-label="Next">›</button>
            <span className="lp-try__bars">{SCENES.map((x, i) => <i key={x} className={i === scene ? 'is-on' : i < scene ? 'is-done' : ''} style={i === scene && visible ? { animationDuration: DUR[x] + 'ms' } : undefined} />)}</span>
          </div>
          <div className="lp-phone" aria-hidden="true">
            <span className="lp-phone__btn lp-phone__btn--a" /><span className="lp-phone__btn lp-phone__btn--b" /><span className="lp-phone__btn lp-phone__btn--p" />
            <div className="lp-phone__scr">
              <span className="lp-phone__isl" />
              <div className="lp-phone__status"><b>02:47</b><span>●●● 5G ▮</span></div>

              {cur !== 'remove' && (
                <div className="lp-sf">
                  <div className={`lp-sf__bar${cur === 'type' ? ' is-typing' : ''}`}>
                    <span className="lp-sf__ic">⌕</span>
                    <span className="lp-sf__q">{cur === 'type' ? t.query.slice(0, typed) : t.query}<i className="lp-sf__caret" /></span>
                  </div>
                  {cur === 'type' && (
                    <div className="lp-sf__sugg">
                      {t.sugg.map(s => <span key={s}>⌕ {s}</span>)}
                    </div>
                  )}
                  {cur === 'type' && (
                    <div className="lp-kb">
                      {['qwertyuiop', 'asdfghjkl', 'zxcvbnm'].map(r => (
                        <div key={r}>{r.split('').map(k => <span key={k} className={t.query[typed - 1] === k ? 'is-hit' : ''}>{k}</span>)}</div>
                      ))}
                      <div><span className="lp-kb__w">123</span><span className="lp-kb__sp">space</span><span className="lp-kb__go">go</span></div>
                    </div>
                  )}
                  {cur === 'block' && (
                    <div className="lp-blk">
                      <img src={`${PUB}/media/glass-lock.webp`} alt="" />
                      <b>{t.blockT}</b>
                      <span>{t.blockP}</span>
                      <em>{t.blockBy}</em>
                    </div>
                  )}
                </div>
              )}

              {cur === 'remove' && (
                <div className="lp-set">
                  <span className="lp-set__back">‹ {t.setBack}</span>
                  <b className="lp-set__t">{t.setTitle}</b>
                  <div className="lp-set__card">
                    <span className="lp-set__logo"><img src={`${PUB}/favicon.svg`} alt="" /></span>
                    <span><b>SELFCNTRL</b><small>{t.setSub}</small></span>
                  </div>
                  <div className="lp-set__rm">
                    <span className="lp-set__btn">{t.setRemove}</span>
                    <span className="lp-set__tap" />
                  </div>
                  <div className="lp-set__alert">
                    <b>{t.alertT}</b>
                    <span>{t.alertP}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="lp-try__chips">
            <span><i>✕</i>{t.chip1}</span>
            <span><i>⏳</i>{t.chip2}</span>
          </div>
        </div>
      </div>
      <div className="lp-wrap lp-try__cta lp-rv">
        <Link to="/register" className="lp-btn lp-btn--main">{t.cta} →</Link>
      </div>
    </section>
  )
}
